// LayoutChoiceCards — 入室画面専用のレイアウト選択 UI (Classic / Modern の 2 枚カード).
//
// 背景 (consult 2026-06-13、ユーザ確定「スタイライズド・モックカード」):
//   - 入室前の選択は、部屋内ヘッダの文字トグル (LayoutToggle) ではなく、両 UI の
//     **簡易モック図 + ラベル + 1 行説明** を載せた選択カードで見せる。新規/旧版
//     ユーザが「どっちがどんな見た目か」を入室前に一目で掴めるようにする gateway。
//   - 永続化先・経路・スマホ非表示判定は文字トグル時代と同一 (= layoutPreference
//     store / useLayoutPreference.setLayout / showToggle)。入室後の resolveLayout が
//     同じ store を読むので、ここで選んだ表示でそのまま開く。
//
// モック図はスタイライズド (= 実スクショではない CSS/SVG の象徴図)。新規 dep を足さず
// inline SVG で、両 UI の骨格の差を象徴的に描く:
//   - Classic= 上部バー + 左の細いシンプルツール列 + 右端近くまで広い中央 canvas +
//     **下段の全幅チャット帯** (= 旧版の決定的特徴)。右サイドバー (メンバー欄) は無し。
//   - Modern= 左サイドバー + 中央 canvas + **右ドックにプレビュー/レイヤー/チャットを
//     縦に積む** (下段チャット帯は無い)。
// 「Classic= 左に細いツール列・下に全幅チャット帯・右サイドバー無し / Modern= 左サイド
// バー + 右ドックに全部積む」が一目で分かることが差別化の肝。骨格のみの象徴図なので
// **UI 微変更では描き直し不要** (= 陳腐化しない軽量図)。色は既存 CSS 変数を直接参照し、
// テーマ追従する。
//
// 純表示 component (props だけで決まる) として書き、node 環境の tree-walk テストで
// 検証する (ReJoinScreen.test.tsx と同流儀)。

import type { LayoutKind } from '../../infrastructure/layoutPreference.js';

/** 各カードのメタ (ラベル + 1 行説明 + a11y ラベル). */
interface LayoutCardMeta {
  readonly kind: LayoutKind;
  readonly label: string;
  readonly blurb: string;
  /** カード全体の aria-label (モック図は aria-hidden なので、ここで意味を補う)。 */
  readonly ariaLabel: string;
}

const CARDS: ReadonlyArray<LayoutCardMeta> = [
  {
    kind: 'classic',
    label: 'Classic',
    blurb: '旧Java版風',
    ariaLabel: 'Classic レイアウト (旧Java版風)',
  },
  {
    kind: 'modern',
    label: 'Modern',
    blurb: 'ペイントソフト風',
    ariaLabel: 'Modern レイアウト (ペイントソフト風)',
  },
];

/**
 * Classic の象徴モック図 (ClassicLayout の実 grid 骨格を踏襲)。
 *   - 最上段: 全幅の薄い上部バー (帯)。
 *   - 左カラム (row 2〜下まで): シンプルな縦のツール列 (小四角を縦に数個 = ペン/色
 *     ボタン群)。白箱の 16 色グリッドはやめ、素朴な点描列に戻した。
 *   - 中央: 白いキャンバス。左ツール列の右を **右端まで全幅** で占有 (フル高の右
 *     サイドバーは無い)。
 *   - 最下段: chat 帯 + member 欄を横並び (実 grid どおり)。チャット帯 = 左寄り
 *     (左ツール列の右端〜中ほど)、その **右側に小さなメンバー欄** (canvas 右端に
 *     揃う)。下段 chat の右に member、が Classic の骨格。
 * 角丸控えめ (rx=1)・密で「旧版」感。全要素 CSS 変数参照でテーマ追従。aria-hidden。
 *
 * `export` = マニュアル (`exportManual.ts`) の「あなたの画面はどれ?」象徴図でも
 * 個別 import して使う (入室画面のカードと同一図を再利用、2 箇所描き直し回避)。
 */
export const ClassicMock = (): React.ReactNode => (
  <svg viewBox="0 0 100 64" className="block h-full w-full" aria-hidden="true" role="presentation">
    {/* 外枠 (アプリ背景)。角丸控えめ = 旧版の素朴さ。 */}
    <rect
      x="1"
      y="1"
      width="98"
      height="62"
      rx="1"
      fill="var(--color-bg)"
      stroke="var(--color-border)"
    />
    {/* 最上段: 全幅の上部バー (薄い帯)。 */}
    <rect
      x="3"
      y="3"
      width="94"
      height="7"
      rx="1"
      fill="var(--color-bg-soft)"
      stroke="var(--color-border)"
      strokeWidth="0.5"
    />
    {/* 左カラム: シンプルな縦ツール列 (小四角を縦に = ペン/色ボタン群)。 */}
    {[13, 21, 29, 37, 45].map((y) => (
      <rect
        key={`tool-${y}`}
        x="4"
        y={y}
        width="11"
        height="6"
        rx="1"
        fill="var(--color-panel)"
        stroke="var(--color-border)"
        strokeWidth="0.5"
      />
    ))}
    {/* 中央: 白いキャンバス。左ツール列の右を右端まで全幅 (フル高右サイドバー無し)。 */}
    <rect x="18" y="12" width="79" height="38" fill="#ffffff" stroke="var(--color-border)" />
    {/* 最下段 左寄り: 横チャット帯 (左ツール列の右端〜中ほど)。 */}
    <rect
      x="18"
      y="52"
      width="55"
      height="9"
      rx="1"
      fill="var(--color-panel)"
      stroke="var(--color-border)"
      strokeWidth="0.5"
    />
    {/* チャット帯内: 発言行の点描 (横線数本)。 */}
    {[54.5, 57, 59.5].map((y) => (
      <rect key={`chat-${y}`} x="20" y={y} width="40" height="1.4" fill="var(--color-text-muted)" />
    ))}
    {/* 最下段 右側: 小さなメンバー欄 (canvas 右端に揃う、下段で chat の右)。 */}
    <rect
      x="76"
      y="52"
      width="21"
      height="9"
      rx="1"
      fill="var(--color-panel)"
      stroke="var(--color-border)"
      strokeWidth="0.5"
    />
    {/* メンバー欄内: 人名行の点描 (短い横線数本)。 */}
    {[54.5, 57, 59.5].map((y) => (
      <rect
        key={`member-${y}`}
        x="78"
        y={y}
        width="13"
        height="1.4"
        fill="var(--color-text-muted)"
      />
    ))}
  </svg>
);

/**
 * Modern の象徴モック図. ドック式クリーン = 左サイドバー (ツール帯 + 下部に
 * カラーホイール) + 中央キャンバス + 右ドック (プレビュー / レイヤー / チャットの
 * 積み)。カラーホイール (色相環) は Modern の左サイドバー下部にあるカラーパネルを
 * 象徴し、Classic (ホイール無し) との差を強める。角丸・余白でクリーンな印象、
 * アクセント色を一点 (= 選択ツール) 置く。aria-hidden。
 *
 * `export` = ClassicMock と同じ理由 (マニュアルの象徴図で再利用)。
 */
export const ModernMock = (): React.ReactNode => (
  <svg viewBox="0 0 100 64" className="block h-full w-full" aria-hidden="true" role="presentation">
    {/* 外枠。角丸大きめ + 余白 = クリーンな新版感。 */}
    <rect
      x="1"
      y="1"
      width="98"
      height="62"
      rx="4"
      fill="var(--color-bg)"
      stroke="var(--color-border)"
    />
    {/* 左サイドバー (ツール帯 + パネルの 1 枚ドック)。 */}
    <rect x="5" y="6" width="16" height="52" rx="2" fill="var(--color-panel)" />
    {/* ツール帯のアクセント (= 選択中ツール 1 点)。 */}
    <rect x="8" y="9" width="10" height="4" rx="1" fill="var(--color-accent)" />
    {[16, 22, 28, 34].map((y) => (
      <rect
        key={`mtool-${y}`}
        x="8"
        y={y}
        width="10"
        height="4"
        rx="1"
        fill="var(--color-bg-soft)"
      />
    ))}
    {/* 左サイドバー下部: カラーホイール (= Modern のカラーパネルを象徴)。ツール帯
        (y≤38) の下、サイドバー下端 (y=58) の手前に小さく配置。他要素と同じく
        ニュートラルな白丸 + テーマ追従リングで象徴 (カラフルにすると図中で浮くため、
        consult 2026-06-13)。直径 ~10px、サイドバー (x:5〜21) の中央 x=13。 */}
    <circle cx="13" cy="49" r="5" fill="#ffffff" stroke="var(--color-border)" strokeWidth="0.5" />
    {/* 中央: キャンバス (白面、余白広め)。 */}
    <rect x="25" y="6" width="44" height="52" rx="2" fill="#ffffff" />
    {/* 右ドック: プレビュー / レイヤー / チャットの積み (角丸カードを縦に)。 */}
    <rect x="73" y="6" width="22" height="14" rx="2" fill="var(--color-panel)" />
    <rect x="73" y="23" width="22" height="14" rx="2" fill="var(--color-panel)" />
    <rect x="73" y="40" width="22" height="18" rx="2" fill="var(--color-panel)" />
  </svg>
);

/**
 * スマホの象徴モック図 (`ModernPhoneShell` の実骨格を踏襲)。入室画面には出さない
 * (スマホでは Classic/Modern の選択自体が無い、§9.4.1 ④) が、マニュアル
 * (`/help/` 冒頭「あなたの画面はどれ?」の 3 択カード) 用に Classic/Modern と同じ
 * 画風・同じ props 形 (props 無しの純表示 fn) で用意しておく。
 *
 * ClassicMock/ModernMock は「ブラウザウィンドウ」骨格 (外枠=アプリ全体) だが、
 * スマホは「端末」骨格 (外枠=端末本体、縦長) で描く方が一目で伝わるため、3 枚を
 * 並べたときに並んで見えるよう同じ viewBox 100×64 の中央に縦長の端末を置く
 * (3 枚並びで高さを揃えるための決定、consult 不要の表現レベルの選択)。
 *   - 端末本体 (縦長の角丸矩形)。
 *   - 上段: 細い部屋名バー。
 *   - 中央: canvas がいっぱいに占有。
 *   - 中央下端に少し重ねて: 排他シート (下段バーの上端からわずかに顔を出す角丸矩形 +
 *     中央にドラッグハンドル)。「シートが下から迫り上がってくる」余地を示す。
 *   - 下段: 太めのツールバー + アイコン 7 個分の丸 (ペン/消しゴム/ブラシ設定/カラー/
 *     レイヤー/チャット/その他)。
 * 色は Classic/Modern と同じ CSS 変数を参照 (テーマ追従、専用の配色を増やさない)。
 * aria-hidden (入室画面では使わないが、将来 UI に出すときも装飾専用の想定)。
 */
export const PhoneLayoutFigure = (): React.ReactNode => (
  <svg viewBox="0 0 100 64" className="block h-full w-full" aria-hidden="true" role="presentation">
    {/* 端末本体 (縦長、中央配置)。 */}
    <rect
      x="30"
      y="2"
      width="40"
      height="60"
      rx="5"
      fill="var(--color-bg)"
      stroke="var(--color-border)"
    />
    {/* 上段: 細い部屋名バー。 */}
    <rect
      x="33"
      y="5"
      width="34"
      height="4"
      rx="1"
      fill="var(--color-bg-soft)"
      stroke="var(--color-border)"
      strokeWidth="0.5"
    />
    {/* 中央: canvas がいっぱいに占有。 */}
    <rect x="33" y="11" width="34" height="33" fill="#ffffff" stroke="var(--color-border)" />
    {/* 排他シート: 下段バーの上端からわずかに顔を出す (= シートの存在を示す)。 */}
    <rect
      x="39"
      y="41"
      width="22"
      height="6"
      rx="1.5"
      fill="var(--color-bg-soft)"
      stroke="var(--color-border)"
      strokeWidth="0.5"
    />
    {/* シートのドラッグハンドル。 */}
    <rect x="47" y="43" width="6" height="1.2" rx="0.6" fill="var(--color-text-muted)" />
    {/* 下段: 太めのツールバー。 */}
    <rect
      x="33"
      y="45"
      width="34"
      height="15"
      rx="1"
      fill="var(--color-panel)"
      stroke="var(--color-border)"
      strokeWidth="0.5"
    />
    {/* ツールバー内: アイコン 7 個分の丸 (ペン/消しゴム/ブラシ設定/カラー/レイヤー/
        チャット/その他)。 */}
    {[36, 40.7, 45.3, 50, 54.7, 59.3, 64].map((x) => (
      <circle key={`ph-tool-${x}`} cx={x} cy="52.5" r="1.4" fill="var(--color-bg-soft)" />
    ))}
  </svg>
);

const MOCKS: Record<LayoutKind, () => React.ReactNode> = {
  classic: ClassicMock,
  modern: ModernMock,
};

export interface LayoutChoiceCardsProps {
  /** 現在の選択 (= layoutPreference store の現在値 / resolveLayout 結果)。 */
  readonly current: LayoutKind;
  /** 選択変更. caller (useLayoutPreference.setLayout) が即 localStorage に永続化する。 */
  readonly onChange: (next: LayoutKind) => void;
  /** 表示してよいか (= スマホでは false → カード群ごと出さない)。 */
  readonly show: boolean;
}

/**
 * 入室画面のレイアウト選択カード群 (Classic / Modern の 2 枚横並び)。
 *
 * - 選択中カードはアクセント枠 (`--color-accent`、ヘッダ LayoutToggle と統一感)。
 * - クリック / キーボード (Space/Enter/矢印) で選択 → onChange → 永続化。
 * - `show=false` (= スマホ) は null (§9.4.1 ④「選べないものを見せない」)。
 * - a11y: `role="radiogroup"` + 各カード `role="radio"` `aria-checked`。
 *   roving tabindex (= 選択中のみ tabindex=0) で矢印キー移動を radiogroup 慣習に合わせる。
 */
export const LayoutChoiceCards = ({
  current,
  onChange,
  show,
}: LayoutChoiceCardsProps): React.ReactNode => {
  if (!show) return null;

  // 矢印キー: 隣のカードへ移動 + 選択 (radiogroup の慣習)。2 枚なので left/up=前、
  // right/down=次 を循環。
  const move = (delta: number): void => {
    const idx = CARDS.findIndex((c) => c.kind === current);
    const base = idx === -1 ? 0 : idx;
    const nextCard = CARDS[(base + delta + CARDS.length) % CARDS.length];
    if (nextCard) onChange(nextCard.kind);
  };

  return (
    <fieldset className="m-0 mb-4 border-0 p-0" data-testid="rejoin-layout-choice">
      <legend className="mb-1 block p-0 text-sm">レイアウト</legend>
      {/* role=radiogroup: 2 枚のモックカードを選択肢として束ねる。legend を
          aria-label 代わりに使えるよう fieldset/legend で包む。 */}
      <div role="radiogroup" aria-label="レイアウト" className="flex gap-2">
        {CARDS.map((card) => {
          const active = card.kind === current;
          const Mock = MOCKS[card.kind];
          return (
            // biome-ignore lint/a11y/useSemanticElements: モック図 (SVG)+ラベル+説明を持つリッチな選択カードは native <input type="radio"> で中身を表現できない。role="radio"+aria-checked+roving tabindex で radiogroup 慣習を満たす。
            <button
              key={card.kind}
              type="button"
              role="radio"
              aria-checked={active}
              aria-label={card.ariaLabel}
              // roving tabindex: 選択中のみ Tab で入れる。未選択カードは矢印で辿る。
              tabIndex={active ? 0 : -1}
              data-testid={`layout-card-${card.kind}`}
              onClick={() => {
                if (!active) onChange(card.kind);
              }}
              onKeyDown={(e) => {
                if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
                  e.preventDefault();
                  move(1);
                } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
                  e.preventDefault();
                  move(-1);
                }
                // Space/Enter は button 既定で onClick に流れる (= 現カード選択は no-op)。
              }}
              className={`flex flex-1 cursor-pointer flex-col items-stretch rounded border-2 p-1.5 text-left transition ${
                active
                  ? 'border-[var(--color-accent)] bg-[var(--color-bg-soft)]'
                  : 'border-[var(--color-border)] bg-[var(--color-bg-soft)] hover:border-[var(--color-text-muted)]'
              }`}
            >
              {/* 上部: スタイライズド・モック図 (~88px 高相当、aria-hidden)。 */}
              <span className="mb-1 block h-[52px] w-full overflow-hidden rounded-sm">
                <Mock />
              </span>
              {/* 下部: ラベル + 1 行説明。 */}
              <span
                className={`block text-sm font-bold ${
                  active ? 'text-[var(--color-accent)]' : 'text-white'
                }`}
              >
                {card.label}
              </span>
              <span className="block text-[11px] leading-tight text-[var(--color-text-muted)]">
                {card.blurb}
              </span>
            </button>
          );
        })}
      </div>
      {/* 補足: 選択が不可逆でないことを明示 (= 入室後もヘッダのトグルでいつでも切替可)。
          選択をためらわせない安心材料。センタリング + やや強調 (consult 2026-06-13)。 */}
      <p className="mt-2 block text-center font-medium text-[12px] text-[var(--color-text)]">
        入室後もいつでも切り替えできます
      </p>
    </fieldset>
  );
};
